import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, ChevronRight, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

function StatusBadge({ user }) {
  if (user.active === false) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-red-600 bg-red-50 px-1.5 py-0.5 rounded-sm border border-red-200">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
        disabled
      </span>
    );
  }
  if (user.email_verified === false) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-sm border border-amber-200">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        unverified
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm border border-emerald-200">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      active
    </span>
  );
}

export default function UsersTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  // Debounce search input by 250ms to avoid hammering the API on every keystroke.
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(id);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.set("q", debouncedQ);
      if (statusFilter !== "all") params.set("status_filter", statusFilter);
      if (roleFilter !== "all") params.set("role", roleFilter);
      params.set("limit", "200");
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, statusFilter, roleFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3" data-testid="users-toolbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email or name…"
            className="pl-9 rounded-sm h-9"
            data-testid="users-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 h-9 rounded-sm" data-testid="users-status-filter">
            <Filter className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
            <SelectItem value="unverified">Unverified</SelectItem>
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-36 h-9 rounded-sm" data-testid="users-role-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="super_admin">Super admin</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs font-mono text-muted-foreground" data-testid="users-total">
          {loading ? <Loader2 className="h-3 w-3 animate-spin inline" /> : `${total} total`}
        </div>
      </div>

      <div className="border border-border bg-card">
        <table className="w-full">
          <thead className="border-b border-border bg-secondary/30">
            <tr className="text-left text-xs uppercase tracking-wider font-mono text-muted-foreground">
              <th className="p-3">Email</th>
              <th className="p-3 hidden md:table-cell">Name</th>
              <th className="p-3">Status</th>
              <th className="p-3 hidden md:table-cell">Role</th>
              <th className="p-3 hidden lg:table-cell text-right">Wallet</th>
              <th className="p-3 hidden xl:table-cell text-right">Chats</th>
              <th className="p-3 hidden xl:table-cell text-right">Files</th>
              <th className="p-3 hidden md:table-cell">Last seen</th>
              <th className="p-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr
                key={u.id}
                className="border-b last:border-0 border-border hover:bg-muted/40"
                data-testid={`user-row-${u.id}`}
              >
                <td className="p-3 font-mono text-sm">{u.email}</td>
                <td className="p-3 hidden md:table-cell">{u.name || "—"}</td>
                <td className="p-3"><StatusBadge user={u} /></td>
                <td className="p-3 hidden md:table-cell">
                  <span className={`text-xs font-mono uppercase ${u.role === "super_admin" ? "text-primary" : "text-muted-foreground"}`}>
                    {u.role}
                  </span>
                </td>
                <td className="p-3 hidden lg:table-cell text-right text-xs font-mono">
                  {u.wallet_balance_tokens != null ? u.wallet_balance_tokens.toLocaleString() : "—"}
                </td>
                <td className="p-3 hidden xl:table-cell text-right text-xs font-mono text-muted-foreground">{u.conversations_count ?? "—"}</td>
                <td className="p-3 hidden xl:table-cell text-right text-xs font-mono text-muted-foreground">{u.files_count ?? "—"}</td>
                <td className="p-3 hidden md:table-cell text-xs font-mono text-muted-foreground">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "—"}
                </td>
                <td className="p-3 text-right">
                  <Link
                    to={`/admin/users/${u.id}`}
                    className="inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline"
                    data-testid={`user-view-${u.id}`}
                  >
                    View <ChevronRight className="h-3 w-3" />
                  </Link>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-sm text-muted-foreground">No users match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
